import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Chip,
  Alert,
  Skeleton,
  Dialog,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import CodeIcon from "@mui/icons-material/Code";
import { useNavigate } from "react-router-dom";
import { layoutCSS } from "../component/common/commonStyle";
import { useGetEmailTemplateQuery, useEditEmailTemplateMutation } from "../store/services/emailApi";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";

// ── Helpers (same extraction logic used in CreateEmailCampaign) ────────────
const extractPreviewText = (htmlContent) => {
  if (!htmlContent) return "No preview available";
  const text = htmlContent
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 110 ? text.substring(0, 110) + "…" : text;
};

const extractSubject = (htmlContent) => {
  if (!htmlContent) return "Email Template";
  const h1Match = htmlContent.match(/<h1[^>]*>(.*?)<\/h1>/i);
  if (h1Match) return h1Match[1].replace(/<[^>]*>/g, "").trim();
  const h2Match = htmlContent.match(/<h2[^>]*>(.*?)<\/h2>/i);
  if (h2Match) return h2Match[1].replace(/<[^>]*>/g, "").trim();
  const pMatch = htmlContent.match(/<p[^>]*>(.*?)<\/p>/i);
  if (pMatch) return pMatch[1].replace(/<[^>]*>/g, "").trim();
  return "Email Template";
};

// ── Style tokens ─────────────────────────────────────────────────────────
const BRAND = "#1976d2";

const cardSx = { borderRadius: "8px", overflow: "hidden" };

// Consistent rounded-corner dialogs — always in px, never a bare number
// (a bare number like `borderRadius: 12` gets multiplied by the MUI theme's
// spacing unit, e.g. 12 * 8px = 96px, which is what produced the "oval").
const dialogPaperSx = { borderRadius: "16px", overflow: "hidden" };

const thCellSx = {
  fontWeight: 700,
  fontSize: "0.75rem",
  color: "#fff",
  py: 1.2,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  borderRight: "0.5px solid rgba(255,255,255,0.25)",
};

const tdCellSx = {
  fontSize: "0.8rem",
  py: 1.2,
};

// ── Miniature HTML thumbnail (scaled-down live render) ──────────────────
function TemplateThumb({ html }) {
  const sanitized = html ? DOMPurify.sanitize(html) : null;

  return (
    <Box
      sx={{
        width: 64,
        height: 48,
        borderRadius: "6px",
        border: "1px solid #E6ECF1",
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#fff",
        flexShrink: 0,
      }}
    >
      {sanitized ? (
        <Box
          sx={{
            transform: "scale(0.16)",
            transformOrigin: "top left",
            width: "625%",
            height: "625%",
            pointerEvents: "none",
            "& *": { pointerEvents: "none" },
          }}
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#F5F7F9",
          }}
        >
          <EmailIcon sx={{ fontSize: 18, color: "#C7D3DC" }} />
        </Box>
      )}
    </Box>
  );
}

// ── Full template preview dialog ─────────────────────────────────────────
function TemplatePreviewDialog({ open, onClose, template }) {
  const sanitized = template?.rawHtml ? DOMPurify.sanitize(template.rawHtml) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 1.75,
          background: `linear-gradient(135deg, ${BRAND} 0%, #0d47a1 100%)`,
          color: "#fff",
        }}
      >
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", width: 36, height: 36 }}>
          <EmailIcon sx={{ fontSize: 18 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.68rem", opacity: 0.85, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Template Preview
          </Typography>
          <Typography
            sx={{ fontSize: "0.95rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={template?.name}
          >
            {template?.name}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5, background: "#F4F8FB" }}>
        <Box sx={{ mb: 1.5 }}>
          <Chip
            size="small"
            label={template?.subject || "No subject"}
            sx={{ fontSize: "0.72rem", background: "#EEF6FC" }}
          />
        </Box>
        <Box
          sx={{
            borderRadius: "10px",
            border: "1px solid #E6ECF1",
            backgroundColor: "#fff",
            p: 2,
            maxHeight: 420,
            overflowY: "auto",
            "& img": { maxWidth: "100%", height: "auto" },
            "& *": { maxWidth: "100%" },
          }}
        >
          {sanitized ? (
            <Box dangerouslySetInnerHTML={{ __html: sanitized }} />
          ) : (
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", textAlign: "center", py: 4 }}>
              No HTML content available
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 2, background: "#F4F8FB", borderTop: "1px solid #E6ECF1" }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: "0.8rem" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Rich-text toolbar button ─────────────────────────────────────────────
function ToolbarButton({ title, onClick, children }) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onMouseDown={(e) => e.preventDefault()} // keep editor selection focused
        onClick={onClick}
        sx={{
          borderRadius: "6px",
          color: "#4A5A68",
          "&:hover": { background: "#EAF4FB", color: BRAND },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
}

// ── WYSIWYG template editor (no HTML knowledge required) ────────────────
// Falls back to a plain "view source" textarea for anyone who does want
// to see/tweak raw markup, toggled explicitly rather than shown by default.
function TemplateBodyEditor({ html, onChange }) {
  const editorRef = useRef(null);
  const [sourceMode, setSourceMode] = useState(false);

  // Push `html` into the contentEditable surface whenever we (re)enter
  // WYSIWYG mode — never while the user is actively typing in it, which
  // would otherwise reset the cursor position on every keystroke.
  useEffect(() => {
    if (!sourceMode && editorRef.current) {
      editorRef.current.innerHTML = html || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode]);

  const exec = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    onChange(editorRef.current?.innerHTML || "");
  };

  const handleLink = () => {
    const url = window.prompt("Link URL");
    if (url) exec("createLink", url);
  };

  return (
    <Box sx={{ border: "1px solid #E6ECF1", borderRadius: "10px", overflow: "hidden", background: "#fff" }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          flexWrap: "wrap",
          px: 1,
          py: 0.75,
          borderBottom: "1px solid #E6ECF1",
          background: "#FAFCFE",
        }}
      >
        <ToolbarButton title="Bold" onClick={() => exec("bold")}>
          <FormatBoldIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>
        <ToolbarButton title="Italic" onClick={() => exec("italic")}>
          <FormatItalicIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>
        <ToolbarButton title="Underline" onClick={() => exec("underline")}>
          <FormatUnderlinedIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        <ToolbarButton title="Bulleted list" onClick={() => exec("insertUnorderedList")}>
          <FormatListBulletedIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" onClick={() => exec("insertOrderedList")}>
          <FormatListNumberedIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        <ToolbarButton title="Align left" onClick={() => exec("justifyLeft")}>
          <FormatAlignLeftIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>
        <ToolbarButton title="Align center" onClick={() => exec("justifyCenter")}>
          <FormatAlignCenterIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>
        <ToolbarButton title="Align right" onClick={() => exec("justifyRight")}>
          <FormatAlignRightIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        <ToolbarButton title="Insert link" onClick={handleLink}>
          <InsertLinkIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>
        <ToolbarButton title="Clear formatting" onClick={() => exec("removeFormat")}>
          <FormatClearIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        <ToolbarButton title="Undo" onClick={() => exec("undo")}>
          <UndoIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>
        <ToolbarButton title="Redo" onClick={() => exec("redo")}>
          <RedoIcon sx={{ fontSize: "1.05rem" }} />
        </ToolbarButton>

        <Box sx={{ flex: 1 }} />

        <FormControlLabel
          sx={{ mr: 0, ml: 1 }}
          control={
            <Switch
              size="small"
              checked={sourceMode}
              onChange={(e) => setSourceMode(e.target.checked)}
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <CodeIcon sx={{ fontSize: "0.95rem", color: "#7A8894" }} />
              <Typography sx={{ fontSize: "0.72rem", color: "#7A8894" }}>View source</Typography>
            </Box>
          }
        />
      </Box>

      {/* Editing surface */}
      {sourceMode ? (
        <TextField
          value={html || ""}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          multiline
          minRows={12}
          maxRows={18}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: { fontFamily: "'Roboto Mono', monospace", fontSize: "0.78rem", p: 2 },
          }}
        />
      ) : (
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          sx={{
            minHeight: 260,
            maxHeight: 420,
            overflowY: "auto",
            p: 2,
            fontSize: "0.88rem",
            lineHeight: 1.6,
            outline: "none",
            "&:empty:before": {
              content: '"Start typing your email content…"',
              color: "#B7C2CB",
            },
            "& img": { maxWidth: "100%", height: "auto" },
            "& a": { color: BRAND },
          }}
        />
      )}
    </Box>
  );
}

// ── Main Component ───────────────────────────────────────────────────────
function EmailTemplatesList() {
  const navigate = useNavigate();

  const {
    data: templatesData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetEmailTemplateQuery();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editTemplate, setEditTemplate] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const [editEmailTemplate, { isLoading: isSaving }] = useEditEmailTemplateMutation();

  // Transform API templates to UI format (same shape as CreateEmailCampaign)
  const templates = useMemo(() => {
    if (!templatesData?.data) return [];
    return templatesData.data.map((tpl) => ({
      id: tpl.email_template_id,
      name: tpl.email_template_name,
      category: "Custom",
      subject: extractSubject(tpl.email_template_data),
      previewText: extractPreviewText(tpl.email_template_data),
      rawHtml: tpl.email_template_data,
    }));
  }, [templatesData]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.subject.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
    );
  }, [templates, search]);

  const paginatedTemplates = filteredTemplates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const openEditDialog = (tpl) => {
    setEditTemplate({ id: tpl.id, name: tpl.name, rawHtml: tpl.rawHtml || "" });
    setIsEditOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!editTemplate) return;
    if (!editTemplate.name?.trim()) {
      toast.error("Template name is required");
      return;
    }
    try {
      const payload = {
        email_template_name: editTemplate.name.trim(),
        email_template_data: DOMPurify.sanitize(editTemplate.rawHtml || ""),
      };
      await editEmailTemplate({ id: editTemplate.id, data: payload }).unwrap();
      toast.success("Template saved");
      setIsEditOpen(false);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to save template");
    }
  };

  return (
    <Box sx={layoutCSS}>
      {/* Page Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <IconButton
          size="small"
          onClick={() => navigate(-1)}
          sx={{ border: "1px solid #e0e0e0", backgroundColor: "#fff" }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#222" }}>
            Email Templates
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>
            {isLoading
              ? "Loading templates…"
              : `${templates.length} template${templates.length === 1 ? "" : "s"} available`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon sx={{ fontSize: "1rem" }} />}
          onClick={() => refetch()}
          disabled={isFetching}
          sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: "8px" }}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate("/email-template")}
          sx={{
            textTransform: "none",
            fontSize: "0.75rem",
            borderRadius: "8px",
            boxShadow: "0 6px 16px rgba(25,118,210,0.3)",
          }}
        >
          New Template
        </Button>
      </Box>

      <Card sx={cardSx}>
        {/* Toolbar */}
        <Box sx={{ p: 2, borderBottom: "1px solid #E6ECF1", display: "flex", gap: 1.5, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search by name, subject, or category…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ flex: 1, maxWidth: 360 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1.1rem", color: "#9AA7B1" }} />
                </InputAdornment>
              ),
              sx: { fontSize: "0.82rem" },
            }}
          />
        </Box>

        {/* Table */}
        {error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ fontSize: "0.8rem" }}>
              Failed to load templates. {error?.data?.message || "Please try again."}
            </Alert>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#0288d1" }}>
                <TableRow>
                  <TableCell sx={{ ...thCellSx, width: 84 }}>Preview</TableCell>
                  <TableCell sx={thCellSx}>Template Name</TableCell>
                  <TableCell sx={thCellSx}>Subject</TableCell>
                  <TableCell sx={{ ...thCellSx, width: 110 }}>Category</TableCell>
                  <TableCell sx={{ ...thCellSx, width: 120, borderRight: "none" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="rounded" width={64} height={48} /></TableCell>
                      <TableCell><Skeleton width="60%" /></TableCell>
                      <TableCell><Skeleton width="80%" /></TableCell>
                      <TableCell><Skeleton width={70} /></TableCell>
                      <TableCell align="center"><Skeleton width={60} sx={{ mx: "auto" }} /></TableCell>
                    </TableRow>
                  ))
                ) : paginatedTemplates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <EmailIcon sx={{ fontSize: 32, color: "#DCE3E9", mb: 1 }} />
                      <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                        {search ? "No templates match your search." : "No templates found."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedTemplates.map((tpl) => (
                    <TableRow key={tpl.id} hover sx={{ "&:hover": { backgroundColor: "#F7FAFD" } }}>
                      <TableCell sx={tdCellSx}>
                        <TemplateThumb html={tpl.rawHtml} />
                      </TableCell>
                      <TableCell sx={tdCellSx}>
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#222" }}>
                          {tpl.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdCellSx}>
                        <Typography sx={{ fontSize: "0.78rem", color: "#666" }}>
                          {tpl.subject}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: "#999", mt: 0.25 }}>
                          {tpl.previewText}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdCellSx}>
                        <Chip label={tpl.category} size="small" sx={{ fontSize: "0.68rem" }} />
                      </TableCell>
                      <TableCell sx={tdCellSx} align="center">
                        <Tooltip title="Preview template">
                          <IconButton size="small" onClick={() => setPreviewTemplate(tpl)} sx={{ color: BRAND }}>
                            <VisibilityIcon sx={{ fontSize: "1.05rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit template">
                          <IconButton size="small" onClick={() => openEditDialog(tpl)} sx={{ color: "#1976d2" }}>
                            <EditIcon sx={{ fontSize: "1.05rem" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && !error && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredTemplates.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: "1px solid #E6ECF1",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.75rem" },
            }}
          />
        )}
      </Card>

      <TemplatePreviewDialog
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        template={previewTemplate}
      />

      {/* Edit Template Dialog */}
      <Dialog
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 1.75,
            background: `linear-gradient(135deg, ${BRAND} 0%, #0d47a1 100%)`,
            color: "#fff",
          }}
        >
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", width: 36, height: 36 }}>
            <EditIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.68rem", opacity: 0.85, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Edit Template
            </Typography>
            <Typography
              sx={{ fontSize: "0.95rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={editTemplate?.name}
            >
              {editTemplate?.name || "Untitled"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setIsEditOpen(false)} sx={{ color: "#fff" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2.5, background: "#F4F8FB" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Template Name"
              size="small"
              value={editTemplate?.name || ""}
              onChange={(e) => setEditTemplate((s) => ({ ...(s || {}), name: e.target.value }))}
              fullWidth
              sx={{ background: "#fff", borderRadius: "8px" }}
            />

            <Box>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#4A5A68", mb: 0.75 }}>
                Email Content
              </Typography>
              {editTemplate && (
                <TemplateBodyEditor
                  html={editTemplate.rawHtml}
                  onChange={(html) => setEditTemplate((s) => ({ ...(s || {}), rawHtml: html }))}
                />
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 2, background: "#F4F8FB", borderTop: "1px solid #E6ECF1" }}>
          <Button onClick={() => setIsEditOpen(false)} sx={{ textTransform: "none", fontSize: "0.8rem" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={isSaving}
            sx={{
              textTransform: "none",
              fontSize: "0.8rem",
              borderRadius: "8px",
              boxShadow: "0 6px 16px rgba(25,118,210,0.3)",
              minWidth: 88,
            }}
          >
            {isSaving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailTemplatesList;